import React, { useState, useRef, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiImage } = FiIcons;

const OptimizedImage = ({ 
  src, 
  alt, 
  className = "", 
  placeholder = "blur",
  priority = false,
  onLoad,
  onError,
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [imageSrc, setImageSrc] = useState('');
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    if (!src) return;

    // Create optimized URL with compression
    const optimizeUrl = (url) => {
      if (url.includes('unsplash.com')) {
        // Optimize Unsplash images
        const baseUrl = url.split('?')[0];
        const params = new URLSearchParams({
          w: '800',
          h: '600',
          fit: 'crop',
          auto: 'format',
          q: '75',
          fm: 'webp'
        });
        return `${baseUrl}?${params.toString()}`;
      }
      return url;
    };

    const optimizedSrc = optimizeUrl(src);

    if (priority) {
      // Load immediately for priority images
      setImageSrc(optimizedSrc);
    } else {
      // Lazy load with Intersection Observer
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setImageSrc(optimizedSrc);
              observerRef.current.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: '50px' }
      );

      if (imgRef.current) {
        observerRef.current.observe(imgRef.current);
      }
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [src, priority]);

  const handleLoad = () => {
    setIsLoaded(true);
    if (onLoad) onLoad();
  };

  const handleError = (e) => {
    setHasError(true);
    if (onError) onError(e);
    
    // Fallback to placeholder
    const fallbackText = encodeURIComponent(alt || 'Image');
    e.target.src = `https://via.placeholder.com/800x600/3B82F6/FFFFFF?text=${fallbackText}`;
  };

  return (
    <div ref={imgRef} className={`relative overflow-hidden ${className}`}>
      {/* Placeholder */}
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
          <SafeIcon icon={FiImage} className="text-gray-400 text-2xl" />
        </div>
      )}
      
      {/* Actual Image */}
      <img
        src={imageSrc}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
        className={`transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'} ${className}`}
        {...props}
      />
    </div>
  );
};

export default OptimizedImage;